// React
import React, { useEffect, useId, useRef } from "react";

// External libraries
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/sharp-solid-svg-icons";
import ReactDOM from "react-dom";

// Internal
import { CloseButton } from "../buttons/close-button.jsx";
import { getDataTestId } from "../functions/data-test-ids.jsx";
import { ModalButtonType } from "../modal/modal-constants.jsx";

// CSS
import "./simple-modal.css";

export const SimpleModalWidth = {
    Normal: 1,
    Narrow: 2
};

export { ModalButtonType };

export function SimpleModalMessage(props) {
    if (props.Spinner) {
        return (
            <div className="eone-ui-simple-modal-message-container">
                <FontAwesomeIcon icon={faSpinner} spin={true} />
                <label className="eone-ui-simple-modal-message" data-testid={props.DataTestId}>{props.Message}</label>
            </div>
        );
    }
    return (
        <div className="eone-ui-simple-modal-message-container">
            <label data-testid={props.DataTestId}>{props.Message}</label>
        </div>
    );
}

const SimpleModalPortal = ({ children }) => {
    const container = document.getElementById("modal-root");
    if (container) {
        return ReactDOM.createPortal(children, container);
    }
    return children;
};

function SimpleModalFooterButton(props) {
    const getTypeClass = () => {
        switch (props.Type) {
            case ModalButtonType.Primary:
                return "eone-ui-simple-modal-footer-button-primary";
            case ModalButtonType.Secondary:
                return "eone-ui-simple-modal-footer-button-secondary";
            case ModalButtonType.Danger:
                return "eone-ui-simple-modal-footer-button-danger";
            default:
                return "eone-ui-simple-modal-footer-button-primary";
        }
    };

    return (
        <button
            type="button"
            className={`eone-ui-simple-modal-footer-button ${getTypeClass()}`}
            title={props.Label}
            onClick={props.OnClick}
            data-testid={props.DataTestId}>
            {props.Label}
        </button>
    );
}

export function SimpleModal(props) {
    const modalRef = useRef(null);
    const previousFocusRef = useRef(null);
    const closeRef = useRef(null);
    const hideCloseRef = useRef(false);
    const titleId = useId();

    closeRef.current = props.Close;
    hideCloseRef.current = props.HideClose;

    useEffect(() => {
        if (!props.Show) return;

        previousFocusRef.current = document.activeElement;

        const modalElement = modalRef.current;
        if (modalElement) {
            const focusableElements = modalElement.querySelectorAll(
                "button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])"
            );
            if (focusableElements.length > 0) {
                focusableElements[0].focus();
            }
        }

        const handleKeyDown = (e) => {
            if (e.key === "Escape" && !hideCloseRef.current) {
                closeRef.current?.();
                return;
            }

            if (e.key === "Tab" && modalElement) {
                const focusableElements = modalElement.querySelectorAll(
                    "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex=\"-1\"])"
                );
                if (focusableElements.length === 0) return;

                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                if (e.shiftKey) {
                    if (document.activeElement === firstElement) {
                        e.preventDefault();
                        lastElement.focus();
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        e.preventDefault();
                        firstElement.focus();
                    }
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            if (previousFocusRef.current && typeof previousFocusRef.current.focus === "function") {
                previousFocusRef.current.focus();
            }
        };
    }, [props.Show]);

    const getModalClass = () => {
        if (props.Width === SimpleModalWidth.Narrow) {
            return "eone-ui-simple-modal eone-ui-simple-modal-narrow";
        }
        return "eone-ui-simple-modal eone-ui-simple-modal-normal";
    };

    if (!props.Show) {
        return null;
    }

    const showHeader = !!props.Title;
    const showFooter = props.Buttons && props.Buttons.length > 0;

    return (
        <SimpleModalPortal>
            <div className="eone-ui-simple-modal-container fade-in">
                <div className={getModalClass()} ref={modalRef} role="dialog" aria-modal="true" aria-labelledby={showHeader ? titleId : undefined}>
                    {showHeader && (
                        <div className="eone-ui-simple-modal-header">
                            <div className="eone-ui-simple-modal-header-title" id={titleId}>{props.Title}</div>
                            <CloseButton
                                Visible={!props.HideClose}
                                Close={props.Close}
                                DataTestId={getDataTestId(props.DataTestId, "close-button")} />
                        </div>
                    )}
                    <div className="eone-ui-simple-modal-body">
                        {props.children}
                    </div>
                    {showFooter && (
                        <div className="eone-ui-simple-modal-footer">
                            {props.Buttons.map((button, index) => (
                                <SimpleModalFooterButton
                                    key={index}
                                    Label={button.Label}
                                    Type={button.Type}
                                    OnClick={button.OnClick}
                                    DataTestId={getDataTestId(props.DataTestId, button.Label)} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </SimpleModalPortal>
    );
}
